import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { investmentAPI } from '../api/investmentAPI';
import { useAuth } from './AuthContext'; // Assuming you have an AuthContext for authentication
import { useLoadingStatus } from './LoadingStatusContext'; // Assuming you have a LoadingStatusContext for loading status
import { useMemo } from 'react';

// Define interfaces for our types
interface Profile {
  id: string;
  name: string;
  investment_profile_template: string;
  // Add other profile properties as needed
}

interface ProfileContextType {
  profiles: Profile[];
  currentProfile: Profile | null;
  currentProfileId: string | null;
  loading: boolean;
  error: string | null;
  refreshProfiles: () => Promise<void>;
  handleProfileSelect: (profileId: string) => void;
}

interface ProfileProviderProps {
  children: ReactNode;
}

// Create context with default values
const ProfileContext = createContext<ProfileContextType | null>(null);

// Provider component
export const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [currentProfile, setCurrentProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth(); // Assuming you have a user object in your AuthContext
  const searchParams = new URLSearchParams(location.search);
  const currentProfileId = searchParams.get('profileId');
  
  const { addStatus, removeStatus } = useLoadingStatus();
  const api = useMemo(() => investmentAPI(addStatus, removeStatus), []);
  // console.log('ProfileContext: Entry')

  // Fetch profiles from API
  const refreshProfiles = useCallback(async () => {
    try {
      // console.log('ProfileContext: Refreshing profiles...')
      setLoading(true);
      setError(null);
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      if (user.role !== 'premium' && user.role !== 'premium_plus') {
         const defaultProfile = {
          id: 'default',
          name: 'Default',
          investment_profile_template: 'Balanced Growth Investor',
        };
        setProfiles([defaultProfile]);
        setCurrentProfile(defaultProfile);
        handleProfileSelect(defaultProfile.id);
      } else {
        const fetchedProfiles = await api.getProfiles();
        if (Array.isArray(fetchedProfiles)) {
          setProfiles(fetchedProfiles);
          
          // If current profile was deleted, select first available
          console.log('ProfileContext: Fetched profiles:', fetchedProfiles);
          if (fetchedProfiles.length > 0 && !fetchedProfiles.find(p => p.id === currentProfile?.id)) {
            console.log('ProfileContext: Current profile not found, selecting first available');
            const firstProfile = fetchedProfiles[0];
            setCurrentProfile(firstProfile);
            handleProfileSelect(firstProfile.id);
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing profiles:', error);
      setError('Failed to load investment profiles');
      setProfiles([]);
      setCurrentProfile(null);
    } finally {
      setLoading(false);
    }
  }, [api, currentProfile?.id, user]);

  // Handle profile selection
  const handleProfileSelect = useCallback((profileId: string) => {
    // Find the selected profile
    const selectedProfile = profiles.find(p => p.id === profileId);
    if (selectedProfile) {
      setCurrentProfile(selectedProfile);
    }
    
    // Create new params, preserving existing ones first
    const currentParams = new URLSearchParams(location.search);
    const newParams = new URLSearchParams();

    // Preserve existing params (like localhost, or any others)
    currentParams.forEach((value, key) => {
      // Don't automatically carry over params we are about to set/override
      if (!['tab', 'profileId', 'profileSection', 'view'].includes(key)) { 
         newParams.set(key, value);
      }
    });

    // Set required params for profile navigation
    newParams.set('tab', 'profiles'); // Assuming profile selection always goes to profiles tab
    newParams.set('profileId', profileId);
    
    // Default to analysis section and dashboard view if not already specified or relevant
    if (!currentParams.has('profileSection') || !currentParams.has('view')) {
        newParams.set('profileSection', 'analysis'); 
        newParams.set('view', 'dashboard'); // Default view within analysis
    } else {
        // Preserve existing section/view if they are already set
        newParams.set('profileSection', currentParams.get('profileSection') || 'analysis');
        newParams.set('view', currentParams.get('view') || 'dashboard');
    }
    console.log('ProfileContext: Navigating to:', `${location.pathname}?${newParams.toString()}`);

    // Navigate to /premium with the combined params
    navigate(`${location.pathname}?${newParams.toString()}`);
  }, [profiles, location.search, navigate]); // Updated dependencies

  // Effect for updating currentProfile when URL profileId changes
  useEffect(() => {
    if (currentProfileId && profiles.length > 0) {
      const profile = profiles.find(p => p.id === currentProfileId);
      if (profile) {
        setCurrentProfile(profile);
      }
    }
  }, [currentProfileId, profiles]);

  const contextValue: ProfileContextType = {
    profiles,
    currentProfile,
    currentProfileId,
    loading,
    error,
    refreshProfiles,
    handleProfileSelect
  };

  return (
    <ProfileContext.Provider value={contextValue}>
      {children}
    </ProfileContext.Provider>
  );
};

// Custom hook to use the profile context
export const useProfile = (): ProfileContextType => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};
